import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { CalculatorOutlined } from '@ant-design/icons';
import { useMemoizedFn, useRequest } from 'ahooks';
import {
  DatePicker,
  Form,
  Popconfirm,
  Select,
  Tooltip,
  Typography,
} from 'antd';
import { routesMap } from 'app/Router';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { isIndustryAvgOrg } from 'domains/orgDomain';
import { RevxButton } from 'components/RevxButton';
import {
  DEFAULT_DATE_FORMAT,
  getFormatDate,
  RFQ_SHIPPING_DATE_FORMAT_LOCAL,
} from 'components/RevxDate';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { UnderlineText } from 'components/RevxTypography';
import { CartLineItem } from 'domains/cartDomain';
import {
  AdjustMarkupPayload,
  JobComparatorPart,
  JobComparatorShipping,
  JobComparatorSvc,
  JobEstimator,
  JobEstimatorComparatorItem,
  JobEstimatorConfirmPaperUpdateParamsColumnType,
  JobSpec,
  SearchScopeEnum,
} from 'domains/jobconfigDomain';
import { useUser } from 'hooks';
import { capitalize, sum } from 'lodash-es';
import moment from 'moment';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import jobEstimatorService from 'services/jobEstimatorService';
import { DEBOUNCE_TIME, MIN_PCT, SHIPPING_WILL_CALL } from 'utils/constants';
import { isSourcer, isSupplier } from 'utils/rbac';
import { AddToCartBlockedTooltip } from '../AddToCartBlockedTooltip';
import { ComparisonPress } from '../ComparisonPress';
import { ComparisonMoney } from './ComparisonMoney';
import { ComparisonParcelCost } from './ComparisonParcelCost';
import { PriceOverride } from './PriceOverride';
import { ProjectJobAddFromEnum } from 'domains/projectDomain';

interface ISourceComparison {
  labelMaxWidth: number;
  maxLabel: number;
  showJobEstimator: {
    list: JobEstimatorComparatorItem[];
    partLabel: JobComparatorPart[];
    svcLabel: JobComparatorSvc[];
    rush?: boolean;
  };
  clickToggle: (x: string) => void;
  isClickToggle: (x: string) => boolean;
  boxHeight: number;
  jobEstimator: JobEstimator | undefined;
  choiceSerialNumber: number | undefined;
  updateComparison: (jobEstimator: JobEstimatorComparatorItem) => Promise<void>;
  setJobEstimatorLoading: Dispatch<SetStateAction<boolean>>;
  canAddToCart: boolean;
  addToCArtHandler?: (e: CartLineItem) => void;
  cancelUpdateMarkup: () => void;
  updateMarkup: (payload: AdjustMarkupPayload) => Promise<void>;
  spec?: JobSpec;
  isExpert?: boolean;
}

export interface ISourceComparisonItem extends ISourceComparison {
  idx: number;
  jobEstimatorItem: JobEstimatorComparatorItem;
  getEligible: (jobEstimatorItem: JobEstimatorComparatorItem) => {
    style: {
      opacity: number;
    };
    priceAdjustedStyle: {
      opacity: number;
    };
    show: boolean;
    disabled: boolean;
  };
  getAverageEligible: (jobEstimatorItem: JobEstimatorComparatorItem) => {
    style: {
      opacity: number;
    };
    priceAdjustedStyle: {
      opacity: number;
    };
    show: boolean;
    disabled: boolean;
  };
}

export const SourceComparisonItem = (props: ISourceComparison) => {
  const { showJobEstimator, maxLabel } = props;

  const getEligible = useMemoizedFn(
    (jobEstimatorItem: JobEstimatorComparatorItem) => {
      return {
        style: {
          opacity:
            jobEstimatorItem.eligible && jobEstimatorItem.averageEligible
              ? 1
              : 0,
        },
        priceAdjustedStyle: {
          opacity:
            (!jobEstimatorItem.eligible && !jobEstimatorItem.priceAdjusted) ||
            !jobEstimatorItem.averageEligible
              ? 0
              : 1,
        },
        show: jobEstimatorItem.eligible && jobEstimatorItem.averageEligible,
        disabled:
          (!jobEstimatorItem.eligible && !jobEstimatorItem.priceAdjusted) ||
          !jobEstimatorItem.averageEligible,
      };
    }
  );

  const getAverageEligible = useMemoizedFn(
    (jobEstimatorItem: JobEstimatorComparatorItem) => {
      return {
        style: { opacity: jobEstimatorItem.averageEligible ? 1 : 0 },
        priceAdjustedStyle: {
          opacity: !jobEstimatorItem.averageEligible ? 0 : 1,
        },
        show: jobEstimatorItem.averageEligible,
        disabled: !jobEstimatorItem.averageEligible,
      };
    }
  );

  return (
    <>
      {showJobEstimator.list?.map((jobEstimatorItem, idx) => {
        // PRD-471: when there is no industry average price the container still inserts a blank
        // stand-in at index 0 (serialNumber -1) so the later columns keep their index. Skip
        // rendering that empty column - returning null leaves every other column's idx untouched.
        if (jobEstimatorItem.serialNumber === -1) {
          return null;
        }
        return (
          <RevxFlexLayout
            direction="column"
            className={`comparison-drawer-label ${
              showJobEstimator.list.length < 5 ? 'border-right' : ''
            } ${
              idx === showJobEstimator.list.length - 1 ? 'border-none' : ''
            } ${
              !jobEstimatorItem.eligible || !jobEstimatorItem.averageEligible
                ? 'noEligible'
                : ''
            }`}
            style={{
              maxWidth: `${maxLabel}px`,
            }}
            key={`${jobEstimatorItem?.comparator?.skuId}item${idx}`}
          >
            <SourceComparisonItemTitle
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              getAverageEligible={getAverageEligible}
              {...props}
            />

            <SourceComparisonItemPart
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              getAverageEligible={getAverageEligible}
              {...props}
            />

            <SourceComparisonItemSvc
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              getAverageEligible={getAverageEligible}
              {...props}
            />

            <SourceComparisonItemSubTotalMarkup
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              getAverageEligible={getAverageEligible}
              {...props}
            />

            <SourceComparisonItemSubTotal
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              getAverageEligible={getAverageEligible}
              {...props}
            />

            <SourceComparisonItemShipping
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              getAverageEligible={getAverageEligible}
              {...props}
            />

            <SourceComparisonItemTotal
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              getAverageEligible={getAverageEligible}
              {...props}
            />

            <SourceComparisonItemAction
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              getAverageEligible={getAverageEligible}
              {...props}
            />
          </RevxFlexLayout>
        );
      })}
    </>
  );
};

export const SourceComparisonItemTitle = (props: ISourceComparisonItem) => {
  const {
    idx,
    jobEstimatorItem,
    showJobEstimator,
    setJobEstimatorLoading,
    jobEstimator,
    choiceSerialNumber,
    updateComparison,
    getEligible,
  } = props;
  const dispatch = useAppDispatch();
  const shipArrivalDate = (shippings: JobComparatorShipping[]) => {
    let shipDate = shippings?.[0]?.readyDate,
      arrialDate = shippings?.[0]?.expectedDeliveryDate;
    shippings?.forEach((shipping) => {
      if (
        moment(shipping.readyDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL).isBefore(
          moment(shipDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL)
        )
      ) {
        shipDate = shipping.readyDate;
      }

      if (
        moment(
          shipping.expectedDeliveryDate,
          RFQ_SHIPPING_DATE_FORMAT_LOCAL
        ).isAfter(moment(arrialDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL))
      ) {
        arrialDate = shipping.expectedDeliveryDate;
      }
    });
    return {
      shipDate: getFormatDate({
        dateStr: moment(shipDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL),
        formatStr: DEFAULT_DATE_FORMAT,
      }),
      arrialDate: getFormatDate({
        dateStr: moment(arrialDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL),
        formatStr: DEFAULT_DATE_FORMAT,
      }),
    };
  };
  const isRush = Boolean(showJobEstimator?.rush);
  const isRevshopPrice =
    idx === 1 &&
    jobEstimator?.quantityItems[choiceSerialNumber || 0].revshopEligible;
  return (
    <>
      <RevxFlexLayout
        className={`comparison-drawer-rfq comparison-drawer-part-press`}
        justify="justify-center"
        align="start"
        direction="column"
      >
        {idx === 0 ? (
          <>
            {/* hidden={!getAverageEligible(jobEstimatorItem).show} */}
            <span className="comparison-drawer-rfq-title">
              <Trans i18nKey="platform.sider.industryAvgTitle"></Trans>
            </span>
          </>
        ) : (
          <>
            {isRevshopPrice && (
              <span className="comparison-drawer-rfq-title">Revshop Price</span>
            )}
            <Select
              className="comparison-drawer-rfq-supplier-select"
              value={jobEstimatorItem.orgId}
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
              onChange={async (e) => {
                setJobEstimatorLoading(true);
                try {
                  const res = await dispatch.jobEstimator.changeOrg({
                    jobConfigId: jobEstimator?.jobConfigId || '',
                    orgId: e,
                    quantityItemSerialNumber: choiceSerialNumber || 0,
                    comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
                  });
                  updateComparison(res);
                } catch {
                  // the request already reported itself - just let go of the spinner so the
                  // comparison stays usable instead of sitting behind a permanent overlay.
                  setJobEstimatorLoading(false);
                }
              }}
            >
              {jobEstimatorItem.optionOrgs.map((item) => (
                <Select.Option value={item.orgId}>
                  <Tooltip title={item.orgName}>{item.orgName}</Tooltip>
                </Select.Option>
              ))}
            </Select>

            <span
              className="comparison-drawer-rfq-supplier-noEligible"
              hidden={getEligible(jobEstimatorItem).show}
            >
              <Trans i18nKey="jobConfig.noPressPaperCombination"></Trans>
            </span>
          </>
        )}
      </RevxFlexLayout>
      <RevxFlexLayout
        align="center"
        justify="left"
        className={`comparison-drawer-part-service ${
          isRush ? 'comparison-drawer-rush' : ''
        }`}
        hidden={!isRush}
        style={{
          height: '38px',
        }}
      >
        <span>
          {shipArrivalDate(jobEstimatorItem.comparator?.shippings).arrialDate}
        </span>
      </RevxFlexLayout>
    </>
  );
};

export const SourceComparisonItemPart = (props: ISourceComparisonItem) => {
  // Both units arrive together; nothing refetches when the buyer switches, so pick here.
  // These arrive as finished labels (`100lb`, `17x11x3 in`), so they are picked, never measured:
  // `display` runs Number() and would turn either of them into undefined.
  const { basisWeight: showBasisWeight } = useMeasurementUnit();
  const {
    idx,
    jobEstimatorItem,
    clickToggle,
    isClickToggle,
    labelMaxWidth,
    jobEstimator,
    choiceSerialNumber,
    updateComparison,
    showJobEstimator,
    setJobEstimatorLoading,
    getEligible,
    getAverageEligible,
    spec,
  } = props;
  const dispatch = useAppDispatch();
  const firstCol = idx === 0;
  const showPaperSelection = (data: JobSpec) => {
    dispatch.jobConfig.createConfirmPaper({
      quantitySerialNumber: choiceSerialNumber || 0,
    });
    dispatch.jobEstimator.setState({
      currentComparablePaper: {
        type: JobEstimatorConfirmPaperUpdateParamsColumnType.INDUSTRY_AVERAGE,
        data,
      },
    });
  };
  return (
    <>
      {jobEstimatorItem.comparator?.parts.map((part, index) => {
        const pressList = (part.optionPresses || []).map((item) => {
          return {
            pressCharge: part.pressCharge,
            pressCode: item.componentCode,
            pressId: item.id,
            pressName: item.componentCodeName,
            name: item.name,
          };
        });
        return (
          <RevxFlexLayout
            direction="column"
            className="comparison-drawer-part"
            key={`${part?.skuId} + ${part?.paperId} + ${part?.pressId}`}
          >
            <RevxFlexLayout
              justify="left"
              className="comparison-drawer-part-label"
              onClick={() => clickToggle(part?.skuName)}
            >
              {getEligible(jobEstimatorItem).show &&
                getAverageEligible(jobEstimatorItem).show && (
                  <ComparisonMoney
                    value={sum([
                      part.paperCharge,
                      part.pressCharge,
                      sum(part.svcs.map((svc) => svc.charge)),
                    ])}
                  />
                )}
            </RevxFlexLayout>
            <RevxFlexLayout
              align="center"
              justify="left"
              className="comparison-drawer-part-press"
              hidden={!isClickToggle(part.skuName)}
              style={getEligible(jobEstimatorItem).style}
            >
              {getEligible(jobEstimatorItem).show && (
                <Form.Item
                  name={[
                    jobEstimatorItem.serialNumber + '',
                    part.skuId + '',
                    'curSelect',
                  ]}
                >
                  <ComparisonPress
                    onChange={async (e) => {
                      setJobEstimatorLoading(true);
                      try {
                        const res = await dispatch.jobEstimator.adjustPress({
                          jobConfigId: jobEstimator?.jobConfigId || '',
                          quantityItemSerialNumber: choiceSerialNumber || 0,
                          comparatorItemSerialNumber:
                            jobEstimatorItem.serialNumber,
                          pressAdjusts: [
                            {
                              skuId: part.skuId,
                              revxPressId: +e,
                            },
                            ...jobEstimatorItem.comparator.parts.filter(
                              (item) => item.skuId !== part.skuId
                            ),
                          ],
                        });

                        updateComparison(res);
                      } catch {
                        setJobEstimatorLoading(false);
                      }
                    }}
                    optionArr={pressList}
                  />
                </Form.Item>
              )}
            </RevxFlexLayout>
            <RevxFlexLayout
              align="top"
              justify="justify-center"
              direction="column"
              className="comparison-drawer-part-service comparison-drawer-part-service-weight"
              hidden={!isClickToggle(part.skuName)}
              style={{
                // marginBottom: part.svcs.length ? '' : '10px',
                ...getEligible(jobEstimatorItem).style,
              }}
            >
              {getEligible(jobEstimatorItem).show && (
                <>
                  <ComparisonMoney value={part.paperCharge} />
                  <span className="comparison-drawer-part-paper-brand">
                    <Typography.Title
                      style={{
                        fontSize: '13px',
                        marginBottom: 0,
                        maxWidth: `${labelMaxWidth - 50}px`,
                      }}
                      ellipsis={{
                        tooltip: `${showBasisWeight(part.paperWeight, {
                          metric: part.paperWeightMetric,
                          withUnit: false,
                        })} ${part.paperCategory
                          .split('_')
                          .map((item) => capitalize(item))
                          .join(' ')}`,
                      }}
                    >
                      {`${showBasisWeight(part.paperWeight, {
                          metric: part.paperWeightMetric,
                          withUnit: false,
                        })} ${part.paperCategory
                        .split('_')
                        .map((item) => capitalize(item))
                        .join(' ')}`}
                    </Typography.Title>
                  </span>
                  <span
                    className="comparison-drawer-default-weight"
                    hidden={!labelMaxWidth}
                  >
                    <Typography.Title
                      style={{
                        fontSize: '13px',
                        marginBottom: 0,
                        maxWidth: `${labelMaxWidth - 50}px`,
                      }}
                      ellipsis={{
                        tooltip: part.paperName || '',
                      }}
                    >
                      {part.paperName || ''}
                    </Typography.Title>
                  </span>
                </>
              )}
            </RevxFlexLayout>
            {spec?.parts[index].paperSearchScope ===
              SearchScopeEnum.MY_PAPER && (
              <RevxFlexLayout
                justify="left"
                className="fixed-height-change-paper"
              >
                {firstCol && (
                  <UnderlineText
                    color="blue"
                    onClick={() => {
                      showPaperSelection(spec?.parts[index]);
                    }}
                  >
                    Change paper
                  </UnderlineText>
                )}
              </RevxFlexLayout>
            )}
            {showJobEstimator?.partLabel
              .find((item) => item.skuId === part.skuId)
              ?.svcs.map((item) => {
                const svcObj = part.svcs.find((svc) => item.name === svc.name);
                return (
                  <RevxFlexLayout
                    align="center"
                    justify="left"
                    className="comparison-drawer-part-service comparison-drawer-part-service-weight"
                    hidden={!isClickToggle(part.skuName)}
                    key={`${svcObj?.charge}part${svcObj?.name}`}
                    style={getEligible(jobEstimatorItem).style}
                  >
                    <ComparisonMoney value={svcObj?.charge || 0} />
                  </RevxFlexLayout>
                );
              })}
          </RevxFlexLayout>
        );
      })}
    </>
  );
};

export const SourceComparisonItemSvc = (props: ISourceComparisonItem) => {
  // Both units arrive together; nothing refetches when the buyer switches, so pick here.
  // These arrive as finished labels (`100lb`, `17x11x3 in`), so they are picked, never measured:
  // `display` runs Number() and would turn either of them into undefined.
  const { sizeFor } = useMeasurementUnit();
  const {
    clickToggle,
    isClickToggle,
    jobEstimatorItem,
    boxHeight,
    showJobEstimator,
    getEligible,
    cancelUpdateMarkup,
    updateMarkup,
    jobEstimator,
    choiceSerialNumber,
  } = props;
  return (
    <RevxFlexLayout direction="column" className="comparison-drawer-part">
      <RevxFlexLayout
        justify="left"
        className="comparison-drawer-part-label"
        onClick={() => clickToggle('Services')}
      >
        {getEligible(jobEstimatorItem).show && (
          <ComparisonMoney
            value={sum([
              ...jobEstimatorItem.comparator?.svcs.map((svc) => svc.charge),
              jobEstimatorItem.comparator?.parcel.charge,
            ])}
          />
        )}
      </RevxFlexLayout>
      {showJobEstimator.svcLabel.map((label, svcIdx) => {
        const svc = jobEstimatorItem.comparator?.svcs.find(
          (item) => item.name === label.name
        );
        return (
          <RevxFlexLayout
            align="center"
            justify="left"
            className="comparison-drawer-wrapper-service comparison-drawer-part-service-weight"
            hidden={!isClickToggle('Services')}
            key={`${svc?.charge}job${svc?.name}`}
            style={{
              marginTop: svcIdx === 0 ? '16px' : '',
              ...getEligible(jobEstimatorItem).style,
            }}
          >
            {svc ? <ComparisonMoney value={svc?.charge || 0} /> : 'None'}
          </RevxFlexLayout>
        );
      })}
      <RevxFlexLayout
        align="top"
        justify="justify-start"
        direction="column"
        className="comparison-drawer-wrapper-service comparison-drawer-part-service-parcel comparison-drawer-part-service-weight jobSvcRef"
        hidden={!isClickToggle('Services')}
        style={{
          paddingTop: '10px',
          minHeight: `${boxHeight}px`,
          ...getEligible(jobEstimatorItem).style,
        }}
      >
        <ComparisonParcelCost
          charge={jobEstimatorItem.comparator?.parcel.charge}
          cancelUpdateMarkup={cancelUpdateMarkup}
          updateMarkup={updateMarkup}
          jobConfigId={jobEstimator?.jobConfigId}
          quantityItemSerialNumber={choiceSerialNumber}
          comparatorItemSerialNumber={jobEstimatorItem.serialNumber}
          boxChargeEditable={jobEstimatorItem.boxChargeEditable}
          paperCharge={jobEstimatorItem.comparator?.parts[0].paperCharge}
        />
        {/* <ComparisonMoney value={jobEstimatorItem.comparator?.parcel.charge} /> */}
        <span
          style={{
            fontSize: '11px',
            fontWeight: 400,
          }}
        >
          {sizeFor(
            jobEstimatorItem.comparator?.parcel.dimensions.join(' | '),
            jobEstimatorItem.comparator?.parcel.dimensionsMetric?.join(' | ')
          )}
        </span>
      </RevxFlexLayout>
    </RevxFlexLayout>
  );
};

export const SourceComparisonItemSubTotal = (props: ISourceComparisonItem) => {
  const { jobEstimatorItem, getEligible } = props;
  return (
    <RevxFlexLayout justify="left" className="comparison-drawer-subtotal">
      {!getEligible(jobEstimatorItem).disabled && (
        <ComparisonMoney
          value={jobEstimatorItem.comparator?.revxPrintingCharge}
        />
      )}
    </RevxFlexLayout>
  );
};

export const SourceComparisonItemSubTotalMarkup = (
  props: ISourceComparisonItem
) => {
  const {
    jobEstimatorItem,
    jobEstimator,
    choiceSerialNumber,
    cancelUpdateMarkup,
    updateMarkup,
    getEligible,
  } = props;
  const [pctValue, setPctValue] = useState<number>();
  const [markupValue, setMarkupValue] = useState<number>();
  useEffect(() => {
    setPctValue(jobEstimatorItem.comparator?.revxMarkupPct);
    setMarkupValue(jobEstimatorItem.comparator?.revxMarkup);
  }, [jobEstimatorItem]);

  return (
    <RevxFlexLayout
      align="center"
      justify="space-between"
      className="comparison-drawer-wrapper-service"
      style={{
        height: '60px',
        ...getEligible(jobEstimatorItem).priceAdjustedStyle,
      }}
    >
      <RevxInputNumber
        className="comparison-drawer-part-pct"
        onChange={async (e) => {
          const value = +e < MIN_PCT ? MIN_PCT : +e;
          if (pctValue === +e) return;
          setPctValue(value);
          cancelUpdateMarkup();
          updateMarkup({
            jobConfigId: jobEstimator?.jobConfigId || '',
            quantityItemSerialNumber: choiceSerialNumber || 0,
            comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
            revxMarkupPct: value,
          });
        }}
        disabled={
          getEligible(jobEstimatorItem).disabled ||
          !jobEstimatorItem.comparator?.revxPrintingCharge
        }
        min={MIN_PCT}
        precision={2}
        value={pctValue}
        suffix="%"
      />
      <RevxInputNumber
        className="comparison-drawer-part-pct"
        value={markupValue}
        onChange={async (e) => {
          setMarkupValue(+e);

          cancelUpdateMarkup();
          updateMarkup({
            jobConfigId: jobEstimator?.jobConfigId || '',
            quantityItemSerialNumber: choiceSerialNumber || 0,
            comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
            revxMarkup: +e,
          });
        }}
        disabled={
          getEligible(jobEstimatorItem).disabled ||
          !jobEstimatorItem.comparator?.revxPrintingCharge
        }
        precision={2}
        prefix="$"
      />
    </RevxFlexLayout>
  );
};

export const SourceComparisonItemShipping = (props: ISourceComparisonItem) => {
  const {
    jobEstimatorItem,
    clickToggle,
    isClickToggle,
    jobEstimator,
    choiceSerialNumber,
    cancelUpdateMarkup,
    updateMarkup,
    getEligible,
    getAverageEligible,
    updateComparison,
    setJobEstimatorLoading,
    isExpert,
  } = props;
  const user = useUser();
  const dispatch = useAppDispatch();
  const totalStep = useAppSelector(
    (state) => state.jobConfig.stepInfos?.length
  );
  const { runAsync, loading } = useRequest(
    dispatch.jobEstimator.changeShipping,
    {
      manual: true,
      debounceWait: DEBOUNCE_TIME,
    }
  );
  const [pctValue, setPctValue] = useState<number>();
  const [markupValue, setMarkupValue] = useState<number>();
  useEffect(() => {
    setPctValue(jobEstimatorItem.comparator?.revxShippingMarkupPct);
    setMarkupValue(jobEstimatorItem.comparator?.revxShippingMarkup);
  }, [jobEstimatorItem]);
  useEffect(() => {
    setJobEstimatorLoading(loading);
  }, [loading, setJobEstimatorLoading]);
  const shipArrivalDate = (shippings: JobComparatorShipping[]) => {
    let shipDate = shippings?.[0]?.readyDate,
      arrialDate = shippings?.[0]?.deliveryDate;
    shippings?.forEach((shipping) => {
      if (
        moment(shipping.readyDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL).isBefore(
          moment(shipDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL)
        )
      ) {
        shipDate = shipping.readyDate;
      }

      if (
        moment(shipping.deliveryDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL).isAfter(
          moment(arrialDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL)
        )
      ) {
        arrialDate = shipping.deliveryDate;
      }
    });
    return {
      shipDate: getFormatDate({
        dateStr: moment(shipDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL),
        formatStr: DEFAULT_DATE_FORMAT,
      }),
      arrialDate: getFormatDate({
        dateStr: moment(arrialDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL),
        formatStr: DEFAULT_DATE_FORMAT,
      }),
    };
  };
  const freeShipping = useMemo(() => {
    return Boolean(
      !jobEstimatorItem.comparator?.shippings.find((item) => !item.freeShipping)
    );
  }, [jobEstimatorItem]);

  const readyDate = useMemo(() => {
    return moment(
      jobEstimatorItem.comparator.shippings?.[0]?.readyDate ||
        jobEstimatorItem.comparator.shippings?.[0]?.suggestReadyDate,
      moment.ISO_8601
    );
  }, [jobEstimatorItem.comparator.shippings]);

  return (
    <RevxFlexLayout direction="column" className="comparison-drawer-wrapper">
      <RevxFlexLayout
        justify="space-between"
        className="comparison-drawer-part-label comparison-drawer-wrapper-label"
        onClick={() => clickToggle('Shipping Total')}
      >
        {!getEligible(jobEstimatorItem).disabled && (
          <>
            {jobEstimatorItem.comparator.shippings.find(
              (item) => item.shippingInfo.carrierService === SHIPPING_WILL_CALL
            ) ? (
              <ComparisonMoney
                value={jobEstimatorItem.comparator?.revxFreightCharge}
              />
            ) : (
              <>
                <ComparisonMoney
                  decoration={freeShipping}
                  value={jobEstimatorItem.comparator?.revxFreightCharge}
                />
                <span
                  style={{
                    fontSize: '12px',
                  }}
                  hidden={!freeShipping}
                >
                  <Trans i18nKey="jobConfig.stepViewQuote.freeShipping"></Trans>
                </span>
              </>
            )}
          </>
        )}
      </RevxFlexLayout>

      <RevxFlexLayout
        align="center"
        justify="left"
        className="comparison-drawer-part-service"
        hidden={!isClickToggle('Shipping Total')}
        style={getEligible(jobEstimatorItem).priceAdjustedStyle}
      >
        <Popconfirm
          placement="top"
          title={<Trans i18nKey="jobConfig.stepComparison.popconfirm"></Trans>}
          icon={null}
          overlayClassName="comparison-drawer-body-confirm jobConfig-popover-source"
          onConfirm={() => {
            if (isExpert) {
              dispatch.jobConfig.setState({
                drawerShowType: false,
              });
              document.getElementById('expert8')?.scrollIntoView();
            } else {
              if (user && (isSourcer(user) || isSupplier(user))) {
                dispatch.jobConfig.goToStep(totalStep - 3);
              } else {
                dispatch.jobConfig.goToStep(totalStep - 2);
              }
            }
          }}
          getPopupContainer={(e) => {
            return e as HTMLElement;
          }}
          okText={<Trans i18nKey="common.confirm"></Trans>}
          cancelText={<Trans i18nKey="common.cancel"></Trans>}
        >
          {jobEstimatorItem.comparator?.shippings.length !== 1 ? (
            <UnderlineText color="blue">{`Split Shipment (${jobEstimatorItem.comparator?.shippings.length})`}</UnderlineText>
          ) : (
            <UnderlineText color="blue">
              {
                jobEstimatorItem.comparator?.shippings[0].shippingInfo
                  .carrierService
              }
            </UnderlineText>
          )}
        </Popconfirm>
      </RevxFlexLayout>

      <RevxFlexLayout
        align="center"
        justify="space-between"
        className="comparison-drawer-wrapper-service"
        style={{
          height: '60px',
          ...getEligible(jobEstimatorItem).priceAdjustedStyle,
        }}
        hidden={!isClickToggle('Shipping Total')}
      >
        <RevxInputNumber
          className="comparison-drawer-part-pct"
          disabled={
            !jobEstimatorItem.comparator?.revxFreightCharge ||
            getEligible(jobEstimatorItem).disabled
          }
          onChange={async (e) => {
            const value = +e < MIN_PCT ? MIN_PCT : +e;
            if (pctValue === +e) return;
            setPctValue(value);

            cancelUpdateMarkup();
            updateMarkup({
              jobConfigId: jobEstimator?.jobConfigId || '',
              quantityItemSerialNumber: choiceSerialNumber || 0,
              comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
              revxShippingMarkupPct: value,
            });
          }}
          min={MIN_PCT}
          value={pctValue}
          suffix="%"
          precision={2}
        />
        <RevxInputNumber
          className="comparison-drawer-part-pct"
          value={markupValue}
          disabled={
            !jobEstimatorItem.comparator?.revxFreightCharge ||
            getEligible(jobEstimatorItem).disabled
          }
          onChange={async (e) => {
            if (markupValue === +e) return;
            setMarkupValue(+e);

            cancelUpdateMarkup();
            updateMarkup({
              jobConfigId: jobEstimator?.jobConfigId || '',
              quantityItemSerialNumber: choiceSerialNumber || 0,
              comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
              revxShippingMarkup: +e,
            });
          }}
          prefix="$"
          precision={2}
        />
      </RevxFlexLayout>

      <RevxFlexLayout
        align="center"
        justify="left"
        className="comparison-drawer-part-service"
        hidden={!isClickToggle('Shipping Total')}
        style={{
          height: '38px',
          ...getAverageEligible(jobEstimatorItem).style,
        }}
      >
        <DatePicker
          allowClear={false}
          size="small"
          defaultValue={readyDate}
          value={readyDate}
          format={DEFAULT_DATE_FORMAT}
          disabledDate={(d) =>
            d &&
            Boolean(
              jobEstimatorItem.comparator.shippings?.[0].orderDate &&
                d < moment(jobEstimatorItem.comparator.shippings?.[0].orderDate)
            )
          }
          onChange={async (e) => {
            const v = await runAsync({
              id: jobEstimator?.jobConfigId || 0,
              orgId: jobEstimatorItem.comparator.orgId,
              qtySerialNumber: choiceSerialNumber || 0,
              comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
              body: {
                readyDate: moment(e).format(RFQ_SHIPPING_DATE_FORMAT_LOCAL),
                shippingAdjusts: (
                  jobEstimatorItem.comparator.shippings || []
                ).map((item) => {
                  return {
                    serialNumber: item.serialNumber,
                    charge: item.freightCost,
                  };
                }),
              },
            });
            updateComparison(v);
          }}
        />
      </RevxFlexLayout>

      <RevxFlexLayout
        align="center"
        justify="left"
        className="comparison-drawer-part-service"
        hidden={!isClickToggle('Shipping Total')}
        style={{
          height: '38px',
          ...getAverageEligible(jobEstimatorItem).style,
        }}
      >
        <span>
          {shipArrivalDate(jobEstimatorItem.comparator?.shippings).arrialDate}
        </span>
      </RevxFlexLayout>
    </RevxFlexLayout>
  );
};

export const SourceComparisonItemTotal = (props: ISourceComparisonItem) => {
  const { jobEstimatorItem, jobEstimator, choiceSerialNumber, getEligible } =
    props;

  return (
    <RevxFlexLayout
      align="center"
      justify="space-between"
      className="comparison-drawer-part-label comparison-drawer-wrapper-label comparison-drawer-part-label-total"
      // style={getEligible(jobEstimatorItem).priceAdjustedStyle}
    >
      {!getEligible(jobEstimatorItem).disabled && (
        <>
          <ComparisonMoney value={jobEstimatorItem.comparator?.totalCharge} />
          {getEligible(jobEstimatorItem).show ? (
            <div
              onClick={() => {
                jobEstimatorService.viewValidatePdf({
                  jobConfigId: jobEstimator?.jobConfigId || '',
                  quantityItemSerialNumber: choiceSerialNumber || 0,
                  comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
                });
              }}
            >
              <CalculatorOutlined style={{ fontSize: '18px' }} />
            </div>
          ) : (
            <div className="comparison-drawer-part-label-eligible">
              <CalculatorOutlined style={{ fontSize: '18px' }} />
            </div>
          )}
        </>
      )}
    </RevxFlexLayout>
  );
};

export const SourceComparisonItemAction = (props: ISourceComparisonItem) => {
  // The wizard's unit, so a job configured in millimetres prints its quote and PO in millimetres.
  const { unit } = useMeasurementUnit();
  const {
    jobEstimator,
    choiceSerialNumber,
    jobEstimatorItem,
    setJobEstimatorLoading,
    canAddToCart,
    getEligible,
    getAverageEligible,
    idx,
    addToCArtHandler,
  } = props;
  /**
   * PRD-471: the comparator says per column whether Add to Cart is blocked, which is what the
   * sourcer needs here - the column can be switched to Industry Average long before the job config
   * knows about it, and until then `industryAverageAddToCartBlocked` on the job config is false.
   * That job config flag stays as a fallback for estimators cached before the column carried one.
   */
  const addToCartBlocked = useAppSelector(
    (state) =>
      jobEstimatorItem.addToCartDisabled ||
      (Boolean(state.jobConfig.configItem?.industryAverageAddToCartBlocked) &&
        isIndustryAvgOrg({ id: jobEstimatorItem.orgId }))
  );
  const dispatch = useAppDispatch();
  const history = useHistory();
  const jobProjectId = useAppSelector((state) => state.project.jobProjectId);
  return (
    <RevxFlexLayout
      align="top"
      justify="left"
      direction="column"
      className="comparison-drawer-body-action"
    >
      <div hidden={idx === 0}>
        {getEligible(jobEstimatorItem).disabled ? (
          <UnderlineText color="gray">
            <Trans i18nKey="jobConfig.viewQuote"></Trans>
          </UnderlineText>
        ) : (
          <a
            href={jobEstimatorService.viewQuotePdf({
              jobConfigId: jobEstimator?.jobConfigId || '',
              quantityItemSerialNumber: choiceSerialNumber || 0,
              comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
              measurementUnit: unit,
            })}
            target="_blank"
            rel="noreferrer"
          >
            <UnderlineText color="blue">
              <Trans i18nKey="jobConfig.viewQuote"></Trans>
            </UnderlineText>
          </a>
        )}
      </div>

      <div hidden={idx === 0}>
        {getEligible(jobEstimatorItem).disabled ? (
          <UnderlineText color="gray">
            <Trans i18nKey="jobConfig.viewPo"></Trans>
          </UnderlineText>
        ) : (
          <a
            href={jobEstimatorService.viewPOPdf({
              jobConfigId: jobEstimator?.jobConfigId || '',
              quantityItemSerialNumber: choiceSerialNumber || 0,
              comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
              measurementUnit: unit,
            })}
            target="_blank"
            rel="noreferrer"
          >
            <UnderlineText color="blue">
              <Trans i18nKey="jobConfig.viewPo"></Trans>
            </UnderlineText>
          </a>
        )}
      </div>

      <div hidden={idx === 0}>
        {getAverageEligible(jobEstimatorItem).disabled ? (
          <UnderlineText color="gray">
            <Trans i18nKey="jobConfig.editPrice"></Trans>
          </UnderlineText>
        ) : (
          <div hidden={!canAddToCart}>
            <PriceOverride {...props} />
          </div>
        )}
      </div>

      {/* PRD-471: the button stays in place so the column keeps its layout, but a blocked
          Industry Average price cannot be checked out - the server refuses the call, and the
          tooltip says so instead of leaving the sourcer guessing at a grey button. */}
      <AddToCartBlockedTooltip blocked={addToCartBlocked}>
        <RevxButton
          type="primary"
          className="comparison-drawer-body-action-button"
          hidden={!canAddToCart || idx === 0}
          disabled={addToCartBlocked || getEligible(jobEstimatorItem).disabled}
          onClick={async () => {
            setJobEstimatorLoading(true);
            dispatch.jobConfig.setRevxSpinloading(true);
            try {
              const cartLineItem = await dispatch.jobEstimator.addToCart({
                jobConfigId: jobEstimator?.jobConfigId || '',
                quantityItemSerialNumber: choiceSerialNumber || 0,
                comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
              });
              if (jobProjectId) {
                await dispatch.project.jobProjectAddJob({
                  projectId: jobProjectId,
                  addFrom: ProjectJobAddFromEnum.SHOP_GALLERY,
                  jobId: cartLineItem.jobId,
                });
                dispatch.project.reloadProjectJobList();
              }
              dispatch.jobConfig.setRevxSpinloading(false);
              if (typeof addToCArtHandler === 'function') {
                addToCArtHandler(cartLineItem);
              } else {
                history.push(routesMap.shoppingCart.path);
              }
            } catch {
              dispatch.jobConfig.setRevxSpinloading(false);
            } finally {
              // whatever the checkout rejected, the sourcer has to be able to go on
              // comparing - never leave the comparison behind its loading overlay.
              setJobEstimatorLoading(false);
            }
          }}
        >
          <Trans
            i18nKey={
              jobProjectId
                ? 'gallery.skuList.addToProject'
                : 'jobConfig.addToCart'
            }
          ></Trans>
        </RevxButton>
      </AddToCartBlockedTooltip>
    </RevxFlexLayout>
  );
};
